import { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import CircularProgress from "@mui/material/CircularProgress";
import { db } from "../../../Utils/Firebase/Firebase";
import SinglePublication from "../../../Components/Update/Publications/SinglePublication";
import useAuthRedirect from "../../../Components/Auth/useAuthRedirect";
import { hasAccess, isSuperAdmin, RESOURCE_TYPES } from "../../../Utils/RBAC/rbacUtils";

const PublicationsList = ({ viewOnly = false }) => {
    const [publications, setPublications] = useState([]);
    const [filteredPublications, setFilteredPublications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    
    useAuthRedirect();

    useEffect(() => {
        const fetchPublications = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, "Researches"));
                const data = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                // Filter based on user's access to specific publications
                let accessiblePublications = data;
                
                if (!isSuperAdmin()) {
                    // Filter to only publications the user has access to
                    const operation = viewOnly ? "read" : "update";
                    accessiblePublications = data.filter(pub => {
                        return hasAccess(RESOURCE_TYPES.PUBLICATIONS, operation, pub.id);
                    });
                }

                // Sort by publicationDate descending
                accessiblePublications.sort((a, b) => new Date(b.publicationDate) - new Date(a.publicationDate));

                setPublications(accessiblePublications);
                setFilteredPublications(accessiblePublications);
            } catch (error) {
                console.error("Error fetching publications:", error);
            }
            setLoading(false);
        };

        fetchPublications();
    }, [viewOnly]);

    // Handle search filter
    useEffect(() => {
        const lowerSearch = search.toLowerCase();
        setFilteredPublications(
            publications.filter(
                (pub) =>
                    pub.title.toLowerCase().includes(lowerSearch) ||
                    pub.publisher?.title?.toLowerCase().includes(lowerSearch) ||
                    pub.publicationType.toLowerCase().includes(lowerSearch)
            )
        );
    }, [search, publications]);

    if (loading) return <CircularProgress className="m-auto" />;

    return (
        <Box mb={2} py={2} className="bg-gray-100 p-4 rounded-lg min-h-[95vh]">
            <Typography variant="h4" textAlign="center" mb={2}>
                {viewOnly ? "View Research Publications" : "Update Research Publications"}
            </Typography>

            {/* Search Input */}
            <FormControl fullWidth>
                <TextField
                    label="Search by Title, Publisher, or Type"
                    fullWidth
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </FormControl>
            {/* Publications List */}
            <Box className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-5">
                {filteredPublications.map((pub) => (
                    <SinglePublication pub={pub} viewOnly={viewOnly} />
                ))}
            </Box>
        </Box>
    );
};

export default PublicationsList;
